import { ns } from '@leverege/i18n';
import { Checkbox, NumericInput, PropertyGrid, Selector, TextInput } from '@leverege/ui-elements';
import { useValueChange } from '@leverege/ui-hooks';
import { ModelEditorFactory, ModelTypeSelector } from '@leverege/ui-plugin';
import React from 'react';
import Colorizers from "../../colorizer/Colorizers.js";
import Variantizers from "../../variantizer/Variantizers.js";
import S from './ColumnEditor.css';
import Model from "./UnitAttributeColumnModel.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t
} = ns('ui-attributes.table.UnitAttributeColumnEditor');
function UnitAttributeColumnEditor(props) {
  const {
    value,
    objectType
  } = props;
  const onModelChange = useValueChange(Model, props);
  if (value == null) {
    return null;
  }
  const formatter = Model.getFormatter(value);
  const formatterEditor = formatter && ModelEditorFactory.create(formatter, {
    eventData: 'setFormatter',
    onChange: onModelChange
  });
  const hFont = ModelEditorFactory.create(Model.getHeaderFont(value), {
    eventData: 'setHeaderFont',
    onChange: onModelChange
  });
  const vFont = ModelEditorFactory.create(Model.getFont(value), {
    eventData: 'setFont',
    onChange: onModelChange
  });
  const uFont = ModelEditorFactory.create(Model.getUnitFont(value), {
    eventData: 'setUnitFont',
    onChange: onModelChange
  });
  return /*#__PURE__*/_jsxs(PropertyGrid, {
    style: {
      backgroundColor: 'var(--color-white)'
    },
    children: [/*#__PURE__*/_jsx(PropertyGrid.Header, {
      style: {
        margin: '10px 0',
        fontSize: 18,
        fontWeight: 'bold'
      },
      title: t('column')
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: t('name'),
      children: /*#__PURE__*/_jsx(TextInput, {
        eventData: "setHeaderLabel",
        hint: t('nameHint'),
        value: Model.getHeaderLabel(value),
        onChange: onModelChange
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: t('width'),
      children: /*#__PURE__*/_jsx(NumericInput, {
        eventData: "setWidth",
        hint: t('widthHint'),
        value: Model.getWidth(value),
        onChange: onModelChange
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: t('minWidth'),
      children: /*#__PURE__*/_jsx(NumericInput, {
        eventData: "setMinWidth",
        hint: t('minWidthHint'),
        value: Model.getMinWidth(value),
        onChange: onModelChange
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: t('maxWidth'),
      children: /*#__PURE__*/_jsx(NumericInput, {
        eventData: "setMaxWidth",
        hint: t('maxWidthHint'),
        value: Model.getMaxWidth(value),
        onChange: onModelChange
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Header, {
      style: {
        margin: '10px 0',
        fontSize: 18,
        fontWeight: 'bold'
      },
      title: t('header')
    }), formatterEditor && /*#__PURE__*/_jsx(PropertyGrid.Item, {
      fill: true,
      inset: true,
      label: t('format'),
      children: formatterEditor
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: t('variant'),
      fill: true,
      children: /*#__PURE__*/_jsx("div", {
        children: ModelTypeSelector.createSelectorAndEditor({
          editorProps: {
            ...props,
            objectType
          },
          modelKey: 'variantizer',
          values: Variantizers.getVariantizersFor(objectType),
          value: Model.getHeaderVariant(value),
          eventData: 'setHeaderVariant',
          onChange: onModelChange
        }, S.fill)
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: t('color'),
      fill: true,
      children: /*#__PURE__*/_jsx("div", {
        children: ModelTypeSelector.createSelectorAndEditor({
          editorProps: {
            objectType,
            ...props
          },
          modelKey: 'colorizer',
          values: Colorizers.getColorizersFor(objectType),
          value: Model.getHeaderColor(value),
          eventData: 'setHeaderColor',
          onChange: onModelChange
        }, S.fill)
      })
    }), hFont != null && /*#__PURE__*/_jsx(PropertyGrid.Item, {
      stretch: true,
      label: t('font'),
      children: hFont
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      stretch: true,
      label: t('justify'),
      children: /*#__PURE__*/_jsx(Selector, {
        value: Model.getHeaderJustify(value),
        eventData: "setHeaderJustify",
        values: [{
          value: null,
          name: t('none')
        }, {
          value: 'left',
          name: t('left')
        }, {
          value: 'center',
          name: t('center')
        }, {
          value: 'right',
          name: t('right')
        }],
        onChange: onModelChange
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      stretch: true,
      label: t('align'),
      children: /*#__PURE__*/_jsx(Selector, {
        eventData: "setHeaderAlign",
        value: Model.getHeaderAlign(value),
        values: [{
          value: null,
          name: t('none')
        }, {
          value: 'top',
          name: t('top')
        }, {
          value: 'center',
          name: t('center')
        }, {
          value: 'bottom',
          name: t('bottom')
        }],
        onChange: onModelChange
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      fillCell: true,
      label: t('textWrap'),
      children: /*#__PURE__*/_jsx(Checkbox, {
        variant: "small",
        value: Model.getHeaderWrap(value),
        eventData: "setHeaderWrap",
        onChange: onModelChange
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: t('background'),
      children: /*#__PURE__*/_jsx(TextInput, {
        eventData: "setHeaderBackground",
        hint: t('backgroundHint'),
        value: Model.getHeaderBackground(value),
        onChange: onModelChange
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Header, {
      style: {
        margin: '10px 0',
        fontSize: 18,
        fontWeight: 'bold'
      },
      title: t('cell')
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: t('emptyValue'),
      children: /*#__PURE__*/_jsx(TextInput, {
        eventData: "setEmptyValue",
        hint: t('emptyValueHint'),
        value: Model.getEmptyValue(value),
        onChange: onModelChange
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: t('variant'),
      fill: true,
      children: /*#__PURE__*/_jsx("div", {
        children: ModelTypeSelector.createSelectorAndEditor({
          editorProps: {
            ...props,
            objectType
          },
          modelKey: 'variantizer',
          values: Variantizers.getVariantizersFor(objectType),
          value: Model.getVariant(value),
          eventData: 'setVariant',
          onChange: onModelChange
        }, S.fill)
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: t('color'),
      fill: true,
      children: /*#__PURE__*/_jsx("div", {
        children: ModelTypeSelector.createSelectorAndEditor({
          editorProps: {
            objectType,
            ...props
          },
          modelKey: 'colorizer',
          values: Colorizers.getColorizersFor(objectType),
          value: Model.getColor(value),
          eventData: 'setColor',
          onChange: onModelChange
        }, S.fill)
      })
    }), vFont != null && /*#__PURE__*/_jsx(PropertyGrid.Item, {
      stretch: true,
      label: t('font'),
      children: vFont
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      stretch: true,
      label: t('justify'),
      children: /*#__PURE__*/_jsx(Selector, {
        value: Model.getJustify(value),
        eventData: "setJustify",
        values: [{
          value: null,
          name: t('none')
        }, {
          value: 'left',
          name: t('left')
        }, {
          value: 'center',
          name: t('center')
        }, {
          value: 'right',
          name: t('right')
        }],
        onChange: onModelChange
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      stretch: true,
      label: t('align'),
      children: /*#__PURE__*/_jsx(Selector, {
        eventData: "setAlign",
        value: Model.getAlign(value),
        values: [{
          value: null,
          name: t('none')
        }, {
          value: 'top',
          name: t('top')
        }, {
          value: 'center',
          name: t('center')
        }, {
          value: 'bottom',
          name: t('bottom')
        }],
        onChange: onModelChange
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      fillCell: true,
      label: t('textWrap'),
      children: /*#__PURE__*/_jsx(Checkbox, {
        variant: "small",
        value: Model.getWrap(value),
        eventData: "setWrap",
        onChange: onModelChange
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: t('background'),
      children: /*#__PURE__*/_jsx(TextInput, {
        eventData: "setBackground",
        hint: t('backgroundHint'),
        value: Model.getBackground(value),
        onChange: onModelChange
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Header, {
      style: {
        margin: '10px 0',
        fontSize: 18,
        fontWeight: 'bold'
      },
      title: "Unit"
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      inset: true,
      label: t('color'),
      fill: true,
      children: /*#__PURE__*/_jsx("div", {
        children: ModelTypeSelector.createSelectorAndEditor({
          editorProps: {
            objectType,
            ...props
          },
          modelKey: 'colorizer',
          values: Colorizers.getColorizersFor(objectType),
          value: Model.getUnitColor(value),
          eventData: 'setUnitColor',
          onChange: onModelChange
        }, S.fill)
      })
    }), uFont != null && /*#__PURE__*/_jsx(PropertyGrid.Item, {
      stretch: true,
      label: t('font'),
      children: uFont
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      stretch: true,
      label: t('align'),
      children: /*#__PURE__*/_jsx(Selector, {
        eventData: "setUnitAlign",
        value: Model.getUnitAlign(value),
        values: [{
          value: null,
          name: t('none')
        }, {
          value: 'top',
          name: t('top')
        }, {
          value: 'center',
          name: t('center')
        }, {
          value: 'bottom',
          name: t('bottom')
        }],
        onChange: onModelChange
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: t('background'),
      children: /*#__PURE__*/_jsx(TextInput, {
        eventData: "setBackground",
        hint: t('backgroundHint'),
        value: Model.getBackground(value),
        onChange: onModelChange
      })
    })]
  });
}
export default /*#__PURE__*/React.memo(UnitAttributeColumnEditor);